import React, { memo, useMemo, useEffect, useRef } from 'react'

import { bool, func, string } from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { resolvePolycountRange } from 'Pods/ThreeDStudio/Services/resolvePolycountRange'
import ButtonGroupSelector from 'Pods/ThreeDStudio/Shared/ButtonGroupSelector/ButtonGroupSelector'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { POLY_COUNT } from 'Pods/ThreeDStudio/Store/constants'

import PolyCountSlider from './Components/PolyCountSlider/PolyCountSlider'

function GenericPolyCountSelector({
  handleLowClick,
  handleMediumClick,
  handleHighClick,
  sliceName,
  setTargetPolycount,
  storeSchemaKey,
  forceDisabled = false,
}) {
  const t = useTranslation()
  const isDisabled = useThreeDStudioStore(state => {
    const uiSchema = state.schema?.[storeSchemaKey]?.ui_schema || {}
    const { face_count: faceCountSchema, target_polycount: targetPolycountSchema } = uiSchema || {}

    return faceCountSchema?.['ui:options']?.disabled || targetPolycountSchema?.['ui:options']?.disabled
  })
  const targetPolycount = useThreeDStudioStore(state => state[sliceName].targetPolycount)
  const faceCountSchema = useThreeDStudioStore(
    state => state.schema?.[storeSchemaKey]?.input_schema?.properties?.face_count,
  )
  const targetPolycountSchema = useThreeDStudioStore(
    state => state.schema?.[storeSchemaKey]?.input_schema?.properties?.target_polycount,
  )
  const polygonType = useThreeDStudioStore(state => state[sliceName]?.polygonType)
  const isSmartLowPoly = useThreeDStudioStore(state => state[sliceName]?.isSmartLowPoly)

  const polyCountSchema = useMemo(() => {
    const schema = faceCountSchema || targetPolycountSchema
    const schemaKey = faceCountSchema ? 'face_count' : 'target_polycount'

    return resolvePolycountRange(schema, schemaKey, { polygonType, isSmartLowPoly })
  }, [faceCountSchema, targetPolycountSchema, polygonType, isSmartLowPoly])
  const { minimum, default: defaultValue, maximum } = polyCountSchema || {}

  const previousRange = useRef({
    minimum: undefined,
    defaultValue: undefined,
    maximum: undefined,
  })

  useEffect(() => {
    const {
      minimum: prevMinimum,
      defaultValue: prevDefaultValue,
      maximum: prevMaximum,
    } = previousRange.current
    const rangeChanged = prevMinimum !== minimum
      || prevMaximum !== maximum
      || prevDefaultValue !== defaultValue

    if (rangeChanged) {
      previousRange.current = { minimum, defaultValue, maximum }

      if (targetPolycount === prevMinimum) {
        handleLowClick(true)
        return
      }
      if (targetPolycount === prevMaximum) {
        handleHighClick(true)
        return
      }
      if (targetPolycount === prevDefaultValue) {
        handleMediumClick(true)
        return
      }

      if (targetPolycount < minimum) {
        handleLowClick(true)
      } else if (targetPolycount > maximum) {
        handleHighClick(true)
      }
    }
  }, [minimum, maximum, defaultValue, targetPolycount])

  const buttons = useMemo(() => [
    {
      id: POLY_COUNT.LOW,
      label: t('shared.polyCount.low'),
      onClick: handleLowClick,
      disabled: forceDisabled || isDisabled,
      isActive: targetPolycount === minimum,
    },
    {
      id: POLY_COUNT.MEDIUM,
      label: t('shared.polyCount.medium'),
      onClick: handleMediumClick,
      disabled: forceDisabled || isDisabled,
      isActive: targetPolycount === defaultValue,
    },
    {
      id: POLY_COUNT.HIGH,
      label: t('shared.polyCount.high'),
      onClick: handleHighClick,
      disabled: forceDisabled || isDisabled,
      isActive: targetPolycount === maximum,
    },
  ], [
    t,
    forceDisabled,
    isDisabled,
    targetPolycount,
    minimum,
    defaultValue,
    maximum,
    handleLowClick,
    handleMediumClick,
    handleHighClick,
  ])

  return (
    <>
      <ButtonGroupSelector
        buttons={buttons}
        keyName="polyCount"
        sliceName={sliceName}
        label={t('shared.polyCount.label')}
      />
      <PolyCountSlider
        isDisabled={forceDisabled || isDisabled}
        setTargetPolycount={setTargetPolycount}
        targetPolycount={Number(targetPolycount || 0)}
        minimum={Number(minimum || 0)}
        maximum={Number(maximum || 0)}
      />
    </>
  )
}

GenericPolyCountSelector.propTypes = {
  handleLowClick: func.isRequired,
  handleMediumClick: func.isRequired,
  handleHighClick: func.isRequired,
  sliceName: string.isRequired,
  setTargetPolycount: func.isRequired,
  storeSchemaKey: string.isRequired,
  forceDisabled: bool,
}

export default memo(GenericPolyCountSelector)
